import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  Tag,
  Descriptions,
  Divider,
  message,
  Modal,
  Form,
  InputNumber,
  Select,
  Timeline,
  Spin,
  Alert
} from 'antd';
import {
  ArrowLeftOutlined,
  UndoOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { billingAdminService, Transaction } from '../../services/billingAdminService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const TransactionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundForm] = Form.useForm();

  useEffect(() => {
    if (user?.type === 'ADMIN' && id) {
      loadTransaction();
    }
  }, [user, id]);

  const loadTransaction = async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getTransactionDetails(id!);
      setTransaction(data);
    } catch (error) {
      console.error('Failed to load transaction:', error);
      message.error('Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (values: { amount?: number; reason?: string }) => {
    if (!transaction) return;

    try {
      setRefundLoading(true);
      await billingAdminService.processRefund(transaction.TransactionID, values);
      message.success('Refund processed successfully');
      setRefundModalVisible(false);
      refundForm.resetFields();
      loadTransaction();
    } catch (error) {
      message.error('Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'succeeded': 'green',
      'pending': 'gold',
      'failed': 'red',
      'refunded': 'purple'
    };
    return statusColors[status] || 'default';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'pending':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'refunded':
        return <ExclamationCircleOutlined style={{ color: '#722ed1' }} />;
      default:
        return null;
    }
  };

  const getTypeColor = (type: string) => {
    const typeColors: Record<string, string> = {
      'payment': 'blue',
      'refund': 'orange',
      'authorization': 'purple'
    };
    return typeColors[type] || 'default';
  };

  // Note: Admin access is enforced by AuthGuard at the route level

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading transaction details...</div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          type="error"
          message="Transaction Not Found"
          description="The transaction you're looking for doesn't exist or has been deleted."
          action={
            <Button onClick={() => navigate('/admin/billing/transactions')}>
              Back to Transactions
            </Button>
          }
        />
      </div>
    );
  }

  const canRefund = transaction.Status === 'succeeded' && transaction.Type === 'payment';

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/billing/transactions')}
          style={{ marginBottom: 16 }}
        >
          Back to Transactions
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Space align="center" size={12}>
              <Title level={2} style={{ margin: 0 }}>
                Transaction Details
              </Title>
              <Tag color={getStatusColor(transaction.Status)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                {getStatusIcon(transaction.Status)} {transaction.Status.toUpperCase()}
              </Tag>
            </Space>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">ID: {transaction.ExternalID}</Text>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(transaction.ExternalID)}
              />
            </div>
          </div>
          {canRefund && (
            <Button
              danger
              icon={<UndoOutlined />}
              onClick={() => {
                setRefundModalVisible(true);
                refundForm.setFieldsValue({ amount: transaction.Amount });
              }}
            >
              Issue Refund
            </Button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Main Details */}
        <div>
          <Card title="Transaction Information">
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Type">
                <Tag color={getTypeColor(transaction.Type)}>{transaction.Type.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(transaction.Status)}>{transaction.Status.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong style={{ fontSize: '18px', color: transaction.Type === 'refund' ? '#cf1322' : '#3f8600' }}>
                  {transaction.Type === 'refund' ? '-' : ''}${transaction.Amount.toFixed(2)} {transaction.Currency}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {dayjs(transaction.CreatedAt).format('MMM D, YYYY HH:mm:ss')}
              </Descriptions.Item>
              {transaction.UpdatedAt && (
                <Descriptions.Item label="Updated At">
                  {dayjs(transaction.UpdatedAt).format('MMM D, YYYY HH:mm:ss')}
                </Descriptions.Item>
              )}
              {transaction.InvoiceRefNo && (
                <Descriptions.Item label="Invoice Reference">
                  <Button
                    type="link"
                    size="small"
                    onClick={() => navigate(`/admin/billing/invoices?search=${transaction.InvoiceRefNo}`)}
                    style={{ padding: 0 }}
                  >
                    {transaction.InvoiceRefNo}
                  </Button>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Customer Info */}
          <Card title="Customer Information" style={{ marginTop: '16px' }}>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Customer Name">
                {transaction.CustomerName || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Customer Email">
                {transaction.CustomerEmail || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Account ID" span={2}>
                <Space>
                  <Text code>{transaction.AccountID}</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(transaction.AccountID)}
                  />
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Payment Method */}
          {(transaction.PaymentMethodLast4 || transaction.PaymentMethodBrand) && (
            <Card title="Payment Method" style={{ marginTop: '16px' }}>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Card Brand">
                  {transaction.PaymentMethodBrand?.toUpperCase() || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Last 4 Digits">
                  ****{transaction.PaymentMethodLast4 || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Razorpay IDs */}
          {(transaction.RazorpayOrderID || transaction.RazorpayPaymentID) && (
            <Card title="Razorpay References" style={{ marginTop: '16px' }}>
              <Descriptions column={1} bordered size="small">
                {transaction.RazorpayOrderID && (
                  <Descriptions.Item label="Order ID">
                    <Space>
                      <Text code style={{ fontSize: '12px' }}>{transaction.RazorpayOrderID}</Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(transaction.RazorpayOrderID!)}
                      />
                    </Space>
                  </Descriptions.Item>
                )}
                {transaction.RazorpayPaymentID && (
                  <Descriptions.Item label="Payment ID">
                    <Space>
                      <Text code style={{ fontSize: '12px' }}>{transaction.RazorpayPaymentID}</Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(transaction.RazorpayPaymentID!)}
                      />
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Failure Details */}
          {transaction.Status === 'failed' && (transaction.FailureCode || transaction.FailureMessage) && (
            <Card title="Failure Details" style={{ marginTop: '16px' }}>
              <Alert
                type="error"
                showIcon
                message={transaction.FailureCode || 'Payment Failed'}
                description={transaction.FailureMessage || 'The payment could not be processed.'}
              />
            </Card>
          )}
        </div>

        {/* Sidebar - Timeline */}
        <div>
          <Card title="Activity Timeline">
            <Timeline
              items={[
                {
                  color: 'blue',
                  children: (
                    <>
                      <Text strong>Transaction Created</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dayjs(transaction.CreatedAt).format('MMM D, YYYY HH:mm:ss')}
                      </Text>
                    </>
                  ),
                },
                ...(transaction.Status === 'succeeded' ? [{
                  color: 'green',
                  children: (
                    <>
                      <Text strong>Payment Succeeded</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Amount: ${transaction.Amount.toFixed(2)}
                      </Text>
                    </>
                  ),
                }] : []),
                ...(transaction.Status === 'failed' ? [{
                  color: 'red',
                  children: (
                    <>
                      <Text strong>Payment Failed</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {transaction.FailureMessage || 'Payment was declined'}
                      </Text>
                    </>
                  ),
                }] : []),
                ...(transaction.Status === 'refunded' ? [{
                  color: 'purple',
                  children: (
                    <>
                      <Text strong>Refund Processed</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dayjs(transaction.UpdatedAt).format('MMM D, YYYY HH:mm:ss')}
                      </Text>
                    </>
                  ),
                }] : []),
              ]}
            />
          </Card>

          {/* Quick Actions */}
          <Card title="Quick Actions" style={{ marginTop: '16px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {canRefund && (
                <Button
                  block
                  danger
                  icon={<UndoOutlined />}
                  onClick={() => {
                    setRefundModalVisible(true);
                    refundForm.setFieldsValue({ amount: transaction.Amount });
                  }}
                >
                  Issue Refund
                </Button>
              )}
              <Button
                block
                onClick={() => copyToClipboard(JSON.stringify(transaction, null, 2))}
              >
                Copy Transaction Data
              </Button>
            </Space>
          </Card>
        </div>
      </div>

      {/* Refund Modal */}
      <Modal
        title="Issue Refund"
        open={refundModalVisible}
        onCancel={() => {
          setRefundModalVisible(false);
          refundForm.resetFields();
        }}
        footer={null}
      >
        <Alert
          type="warning"
          message="Refund Warning"
          description="Refunds are irreversible. Please verify the amount before proceeding."
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={refundForm} onFinish={handleRefund} layout="vertical">
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Original Transaction">
              {transaction.ExternalID}
            </Descriptions.Item>
            <Descriptions.Item label="Original Amount">
              <Text strong>${transaction.Amount.toFixed(2)} {transaction.Currency}</Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Form.Item
            name="amount"
            label="Refund Amount"
            rules={[
              { required: true, message: 'Please enter refund amount' },
              {
                validator: (_, value) => {
                  if (value && value > transaction.Amount) {
                    return Promise.reject('Refund amount cannot exceed original amount');
                  }
                  if (value && value <= 0) {
                    return Promise.reject('Refund amount must be greater than 0');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.01}
              max={transaction.Amount}
              precision={2}
              prefix="$"
              placeholder="Enter refund amount"
            />
          </Form.Item>

          <Form.Item name="reason" label="Reason for Refund">
            <Select placeholder="Select a reason">
              <Option value="duplicate">Duplicate payment</Option>
              <Option value="fraudulent">Fraudulent transaction</Option>
              <Option value="requested_by_customer">Requested by customer</Option>
              <Option value="product_not_received">Product not received</Option>
              <Option value="product_unacceptable">Product not as described</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setRefundModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" danger htmlType="submit" loading={refundLoading}>
                Process Refund
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TransactionDetail;
